// Copying a value a page shows, and saying what was copied (#2344).
//
// An identifier — a serial, a MAC, a UUID — is copied whole, whatever the page
// draws: a UUID shortened to its last eight characters on screen goes to the
// clipboard entire. The toast names the kind of value and the value, so a copy
// from the wrong row is noticed at once; a refused copy says that nothing was
// copied, the way `CopyPassword` does. This is for values that are not secret.
// A site's password, a generated key and a service principal's token keep their
// own acts (ADR-0136), and a network's key its reveal (ADR-0062); none of them
// calls this.

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A value as a short line shows it: a UUID by its last eight characters. */
export function shortValue(value: string): string {
  return UUID.test(value) ? `…${value.slice(-8)}` : value;
}

let hide: ReturnType<typeof setTimeout> | undefined;

/** Says `text` in the shell's one polite status line, at the foot of the
 * window, for a few seconds. One line for the whole application: a second
 * message replaces the first. */
export function announce(text: string): void {
  if (typeof document === "undefined") return;
  let el = document.getElementById("zenith-toast");
  if (!el) {
    el = document.createElement("div");
    el.id = "zenith-toast";
    el.className = "toast";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    document.body.appendChild(el);
  }
  el.textContent = text;
  el.classList.add("show");
  if (hide) clearTimeout(hide);
  const shown = el;
  hide = setTimeout(() => shown.classList.remove("show"), 2600);
}

/** Writes `value` to the clipboard and says so, naming it as `what`
 * ("Serial", "ID"). Resolves to whether it was copied. */
export async function copyValue(value: string, what: string): Promise<boolean> {
  const clip = typeof navigator !== "undefined" ? navigator.clipboard : undefined;
  if (typeof clip?.writeText !== "function") {
    announce("This browser will not let the page reach the clipboard. Nothing was copied.");
    return false;
  }
  try {
    await clip.writeText(value);
    announce(`${what} ${shortValue(value)} copied`);
    return true;
  } catch {
    announce("The browser refused the copy. Nothing was copied.");
    return false;
  }
}
